import 'regenerator-runtime/runtime';
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import {
  msalConfig,
  apiLoginRequest,
  skllsMsalConfig,
  hasUserLoggedOut,
  clearLogoutFlag,
  markSignedIn
} from '@/authConfig.ts';
import { PublicClientApplication } from '@azure/msal-browser';
import Main from './main.tsx';
import { MsalProvider } from '@azure/msal-react';
import { axiosInstance } from './axiosInstance.ts';
import { authTelemetry } from '@/app/services/authTelemetry';
import { createAccessTokenProvider } from '@/app/services/authToken.ts';
import { isHeiDomain } from '@/app/utils/heiDomain.ts';
import { NEEDS_CONSENT_FLAG_KEY, sanitizeRedirectPath } from '@/app/hooks/useLogin.ts';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider as MuiStylesThemeProvider } from '@mui/styles';
import { CssBaseline, GlobalStyles } from '@mui/material';
import theme from './app/styles/theme.ts';

// Force login with guest user in sklls if account is a work/school account and sklls sign in does not work properly
const useExternalInstance =
  window.location.search === '?external=true' || window.location.pathname === '/loginExternal';
const msalInstance = new PublicClientApplication(useExternalInstance ? msalConfig : skllsMsalConfig);

// Set active account on initialization
msalInstance.initialize().then(() => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    // Set the first account as active
    msalInstance.setActiveAccount(accounts[0]);
    // Backfill the returning-visitor marker for already-signed-in browsers.
    markSignedIn();
  }

  msalInstance
    .handleRedirectPromise()
    .then((response) => {
      if (response && response.account) {
        msalInstance.setActiveAccount(response.account);
        clearLogoutFlag();
        // Remember the sign-in so the login page greets them with "Welcome back" next time.
        markSignedIn();
        authTelemetry.trackSessionStart(response.account, 'redirect');

        // MSAL already navigated back to the URL where login started
        // (navigateToLoginRequestUrl default); only force a navigation when a
        // stored deep link or the HEI domain says somewhere else.
        const redirectPath = isHeiDomain(window.location.hostname)
          ? '/translator'
          : sanitizeRedirectPath(localStorage.getItem('postLoginRedirectPath'));
        localStorage.removeItem('postLoginRedirectPath');
        const currentPath = window.location.pathname + window.location.search + window.location.hash;
        if (redirectPath && redirectPath !== currentPath) {
          window.location.href = redirectPath;
        }
      }
    })
    .catch((e) => {
      // Surface the "backend app needs consent" case on the login page after
      // the redirect round-trip; other errors are tracked via telemetry.
      if (e?.errorMessage?.startsWith('AADSTS650052')) {
        sessionStorage.setItem(NEEDS_CONSENT_FLAG_KEY, '1');
      }
    });
});
// create queryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1
    }
  }
});

const acquireAccessToken = createAccessTokenProvider({
  msalInstance,
  apiLoginRequest,
  authTelemetry,
  hasUserLoggedOut,
  clearLogoutFlag
});

axiosInstance.interceptors.request.use(async (config) => {
  const token = await acquireAccessToken();

  config.headers.Authorization = `Bearer ${token}`;

  return config;
});

const container = document.getElementById('root');
const root = createRoot(container!); // TypeScript assertion for non-null

root.render(
  // `enableCssLayer` emits MUI styles into the `mui` cascade layer; the
  // GlobalStyles declaration fixes the layer order so Tailwind's `utilities`
  // layer outranks `mui` and utility classes reliably override MUI styles.
  <StyledEngineProvider injectFirst enableCssLayer>
    <GlobalStyles styles="@layer theme, base, mui, components, utilities;" />
    <MuiStylesThemeProvider theme={theme}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <Main />
          </QueryClientProvider>
        </MsalProvider>
      </ThemeProvider>
    </MuiStylesThemeProvider>
  </StyledEngineProvider>
);
